import { Injectable } from '@angular/core';
import { SignalingService } from './signaling.service';
import { AppointmentMessagingService } from './messaging/appointment-messaging.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { EmployeeMessagingService } from './messaging/employee-messaging.service';
import { InquiryMessagingService } from './messaging/inquiry-messaging.service';
import { NotificationCenterMessagingService } from './messaging/notification-center-messaging.service';
import { PlanMessagingService } from './messaging/plan-messaging.service';
import { SignalingApiService } from '../api/signaling-api.service';
import { ReportGenerationMessagingService } from './messaging/report-generation-messaging.service';
import { ReportGenerationRecoveryStore } from '../report-generation-recovery.store';

@Injectable({ providedIn: 'root' })
/**
 * High-level orchestrator for helper-client signaling startup/shutdown.
 *
 * Responsibilities:
 * - establish the user SignalR group connection once per authenticated session
 * - register app-level message listeners once (to avoid duplicate events)
 * - mirror hub reconnect lifecycle into local connection state flags
 */
export class SignalingConnectionService {
  private readonly isConnected$ = new BehaviorSubject(false);
  private readonly isConnecting$ = new BehaviorSubject(false);
  private listenersRegistered = false;

  constructor(
    private readonly signalingService: SignalingService,
    private readonly signalingApiService: SignalingApiService,
    private readonly appointmentMessagingService: AppointmentMessagingService,
    private readonly employeeMessagingService: EmployeeMessagingService,
    private readonly inquiryMessagingService: InquiryMessagingService,
    private readonly notificationCenterMessagingService: NotificationCenterMessagingService,
    private readonly planMessagingService: PlanMessagingService,
    private readonly reportGenerationMessagingService: ReportGenerationMessagingService,
    private readonly reportGenerationRecoveryStore: ReportGenerationRecoveryStore,
  ) {
    const hubConnection = this.signalingService.getHubConnection();

    hubConnection.onreconnecting(() => {
      this.isConnected$.next(false);
      this.isConnecting$.next(true);
    });

    hubConnection.onreconnected(() => {
      this.isConnected$.next(true);
      this.isConnecting$.next(false);
      void this.reportGenerationRecoveryStore.recoverActiveGenerations();
    });

    hubConnection.onclose(() => {
      this.isConnected$.next(false);
      this.isConnecting$.next(false);
    });
  }

  connect(userId: string) {
    if (!this.isConnected$.value && !this.isConnecting$.value) {
      this.isConnecting$.next(true);
      void this.connectWithConnectionInfo(userId);
    }
  }

  disconnect() {
    // Keep local state in sync immediately; service handles async stop details.
    this.isConnected$.next(false);
    this.isConnecting$.next(false);
    void this.signalingService.closeConnection();
  }

  private registerListenersOnce(): void {
    // SignalR handlers persist on the same hub connection instance, so only wire once.
    if (this.listenersRegistered) {
      return;
    }

    this.appointmentMessagingService.addAppointmentListener();
    this.employeeMessagingService.addEmployeeMessageListener();
    this.inquiryMessagingService.addInquiryListener();
    this.notificationCenterMessagingService.addNotificationCenterListener();
    this.planMessagingService.addPlanListener();
    this.reportGenerationMessagingService.addReportGenerationListener();

    this.listenersRegistered = true;
  }

  private async connectWithConnectionInfo(userId: string): Promise<void> {
    let resolvedUserId = userId;
    let signalingConnectToken: string | undefined;

    try {
      const connectionInfo = await firstValueFrom(
        this.signalingApiService.getSignalingConnectionInfo(),
      );
      resolvedUserId = connectionInfo?.userId ?? userId;
      signalingConnectToken =
        connectionInfo?.signalingConnectToken ?? undefined;
    } catch {
      // Fall back to local user id if signaling connection-info cannot be loaded.
    }

    try {
      await this.signalingService.joinSignalingGroup(
        resolvedUserId,
        signalingConnectToken,
      );
      this.registerListenersOnce();
      this.isConnected$.next(true);
      void this.reportGenerationRecoveryStore.recoverActiveGenerations();
    } catch {
      this.isConnected$.next(false);
    } finally {
      this.isConnecting$.next(false);
    }
  }
}
